import useDjangoWaffle from "@shared/hooks/djangoWaffle/useDjangoWaffle";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { RealtimeConnectionInfo } from "@shared/modules/realtime/data/types/realtime";
import { useRealtimeConnection } from "@shared/modules/realtime/hooks/useRealtimeConnection";
import { useCallback } from "react";
import { useSeatingState } from "../../../contexts/useSeatingState";
import {
  REALTIME_SEATING_CONFIG,
  getSeatingChannel,
} from "../constants/realtime-seating-config";
import { RealtimeSeatingMessage } from "../data/types/realtime-seating";
import {
  parseSeatingMessage,
  processRealtimeSeatingMessage,
} from "../utils/realtime-seating-utils";

type UseRealtimeSeatingStatusReturn = {
  isEnabled: boolean;
  connectionInfo: RealtimeConnectionInfo;
};

export function useRealtimeSeatingStatus(): UseRealtimeSeatingStatusReturn {
  const { isFlagActive } = useDjangoWaffle();
  const { basketId, isBasketUpdating } = useBasketService();
  const {
    isOnSeatingMapPage,
    isLoadingMap,
    seatingEventData,
    seatingObjectArray,
    seatingServiceState,
    stageServiceState,
    updateSeatingState,
    disableBestAvailableSeating,
  } = useSeatingState();

  const isEnabled = isFlagActive(
    REALTIME_SEATING_CONFIG.FEATURE_FLAG,
    seatingServiceState.venueId
  );

  const handleMessage = useCallback(
    (message: RealtimeSeatingMessage): void => {
      // Skip if this is our own basket's update
      if (message.basket_id && basketId && message.basket_id === basketId) {
        return;
      }

      // Skip if we're in the middle of creating a basket (race condition)
      // When basketId is undefined but isBasketUpdating is true, we're creating
      // a basket and this message is likely about our own reservation
      if (message.basket_id && !basketId && isBasketUpdating) {
        return;
      }

      processRealtimeSeatingMessage(
        message,
        seatingServiceState,
        stageServiceState,
        seatingObjectArray.map((seat) => seat.id)
      );
      updateSeatingState();
    },
    [
      basketId,
      isBasketUpdating,
      seatingObjectArray,
      seatingServiceState,
      stageServiceState,
      updateSeatingState,
    ]
  );

  const { connectionInfo } = useRealtimeConnection<RealtimeSeatingMessage>({
    url: REALTIME_SEATING_CONFIG.CENTRIFUGO_URL,
    channel:
      seatingEventData?.id && seatingServiceState.mapItemType
        ? getSeatingChannel(
            seatingEventData.id,
            seatingServiceState.mapItemType
          )
        : undefined,
    enabled:
      isEnabled &&
      isOnSeatingMapPage &&
      !isLoadingMap &&
      disableBestAvailableSeating,
    parseMessage: parseSeatingMessage,
    onMessage: handleMessage,
    reconnectDelayMs: REALTIME_SEATING_CONFIG.RECONNECT_DELAY_MS,
    maxReconnectDelayMs: REALTIME_SEATING_CONFIG.MAX_RECONNECT_DELAY_MS,
  });

  return {
    isEnabled,
    connectionInfo,
  };
}
